import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { MaskLines } from "@/components/Reveal";
import { BRANDS, HOTSPOTS, IMAGES } from "@/data/content";

function Hotspot({ data, lang }) {
  const [open, setOpen] = useState(false);
  const c = data[lang];
  const left = data.side === "left";
  return (
    <div
      className="absolute z-20"
      style={{ left: `${data.x}%`, top: `${data.y}%` }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`hotspot-${data.id}`}
      data-cursor
    >
      <span className="relative flex items-center justify-center w-8 h-8 -translate-x-1/2 -translate-y-1/2">
        {/* glow halo */}
        <span className={`absolute w-8 h-8 rounded-full bg-cyan/30 blur-md transition-all duration-300 ${open ? "scale-150 bg-brass/50" : "animate-pulse"}`} />
        {/* expanding sonar ring */}
        <span className="absolute w-4 h-4 rounded-full border border-brass/70 animate-ping-ring" />
        {/* 4-point star */}
        <svg viewBox="0 0 24 24" className={`relative transition-all duration-300 ${open ? "w-7 h-7 rotate-90" : "w-5 h-5"}`}
          style={{ filter: `drop-shadow(0 0 ${open ? 10 : 5}px ${open ? "#C8862E" : "#0096C7"})` }}>
          <path d="M12 0 L14.2 9.8 L24 12 L14.2 14.2 L12 24 L9.8 14.2 L0 12 L9.8 9.8 Z"
            fill={open ? "#F5C877" : "#7FE3FF"} />
        </svg>
      </span>
      <motion.div
        initial={false}
        animate={{ opacity: open ? 1 : 0, x: open ? 0 : (left ? 8 : -8) }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className={`absolute top-1/2 -translate-y-1/2 ${left ? "right-6" : "left-6"} pointer-events-none`}
      >
        <div className={`flex items-center ${left ? "flex-row-reverse" : ""}`}>
          <span className="h-px w-10 bg-brass/70" />
          <div className="glass-dark rounded-lg p-3 w-56 text-left">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-brass mb-1">{data.id}</p>
            <p className="font-display text-white text-lg leading-tight mb-1">{c.t}</p>
            <p className="text-slate text-xs leading-snug">{c.d}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function Hero() {
  const { t, lang } = useApp();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.12, 1.28]);
  const imgFilter = useTransform(scrollYProgress, [0, 1], ["brightness(1) saturate(1.05)", "brightness(0.6) saturate(0.9)"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.95]);
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  const stats = [[t.hero.stat1, t.hero.stat1l], [t.hero.stat2, t.hero.stat2l], [t.hero.stat3, t.hero.stat3l]];

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden bg-ink" data-testid="hero-section">
      {/* cinematic image with parallax + depth-of-field */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.img
          src={IMAGES.heroSuperyachtNight} alt="Superyacht at night"
          style={{ y: imgY, scale: imgScale, filter: imgFilter }}
          className="w-full h-full object-cover object-center will-change-transform"
          data-testid="hero-image"
        />
        {/* deep ink gradient — pulls the bottom of the image into darkness */}
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/25" />
        {/* radial vignette — draws the eye to the yacht */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(120% 80% at 50% 30%, transparent 25%, rgba(6,15,25,0.85) 100%)" }} />
        {/* filmic top-fade for cinematic depth */}
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink/70 to-transparent pointer-events-none" />
        {/* cold cyan atmosphere wash */}
        <div className="absolute inset-0 mix-blend-overlay pointer-events-none"
             style={{ background: "linear-gradient(180deg, rgba(0,150,199,0.08) 0%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.25) 100%)" }} />
        {/* anchored hotspots */}
        <div className="absolute inset-0 hidden md:block">
          {HOTSPOTS.map((h) => <Hotspot key={h.id} data={h} lang={lang} />)}
        </div>
      </div>

      {/* content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-end pb-28 pt-40">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="font-mono text-[11px] md:text-xs tracking-[0.32em] uppercase text-brass mb-6">{t.hero.overline}</motion.p>

        <h1 className="font-display text-white text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.92] tracking-tight max-w-5xl">
          <MaskLines lines={t.hero.titleLines} delay={0.35} />
        </h1>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
            className="max-w-md text-base md:text-lg text-slate leading-relaxed">
            {t.hero.sub}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
            className="flex flex-wrap gap-4">
            <button onClick={() => go("#contact")} data-testid="hero-cta-primary"
              className="font-mono text-xs uppercase tracking-wider bg-brass text-ink px-7 py-4 rounded-sm hover:bg-brass/90 hover:-translate-y-0.5 transition-all duration-300">
              {t.hero.cta}
            </button>
            <button onClick={() => go("#services")} data-testid="hero-cta-secondary"
              className="font-mono text-xs uppercase tracking-wider border border-white/40 text-white px-7 py-4 rounded-sm hover:border-white hover:bg-white/5 transition-all duration-300">
              {t.hero.cta2}
            </button>
          </motion.div>
        </div>

        {/* stats bar */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="mt-14 grid grid-cols-3 max-w-lg gap-8 pt-8">
          {stats.map(([v, l]) => (
            <div key={l}>
              <p className="font-display text-white text-2xl md:text-3xl leading-none">{v}</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate mt-2">{l}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* hint + scroll */}
      <div className="absolute bottom-8 right-6 lg:right-10 z-10 hidden md:flex items-center gap-2 font-mono text-xs text-brass">
        <span className="inline-block w-8 h-px bg-brass" />{t.hero.hint}
      </div>
      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70"
        animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
        <ChevronDown className="w-5 h-5" />
      </motion.div>

      {/* brand marquee */}
      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 overflow-hidden py-4 bg-ink/60 backdrop-blur-sm">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...BRANDS, ...BRANDS, ...BRANDS].map((b, i) => (
            <span key={`${b}-${i}`} className="font-mono text-xs tracking-[0.35em] text-slate/70 mx-10">{b}</span>
          ))}
        </div>
      </div>
    </section>
  );
}
